import { Injectable, inject, signal } from '@angular/core';
import { finalize, tap } from 'rxjs';

import {
  BookingRequestDetailsModel,
  GoogleAnalyticsEventCategory,
  GoogleAnalyticsEventName,
  GoogleAnalyticsService,
  SERVICE_MODEL_ARRAY,
  Service,
  ServiceViewModel,
} from '@bookly/shared';

import { BookingApiService } from './booking-api.service';

export type BookingWithConvertedServices = Omit<
  BookingRequestDetailsModel,
  'services'
> & {
  services: ServiceViewModel[];
};

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  readonly #bookingApiService = inject(BookingApiService);
  readonly #googleAnalyticsService = inject(GoogleAnalyticsService);

  readonly #upcomingBookingsSignal = signal<BookingWithConvertedServices[]>([]);
  readonly #isUpcomingBookingsLoadingSignal = signal(false);
  readonly #pastBookingsSignal = signal<BookingWithConvertedServices[]>([]);
  readonly #isPastBookingsLoadingSignal = signal(false);
  readonly #bookingDetailsSignal = signal<BookingRequestDetailsModel | null>(
    null
  );
  readonly #isBookingDetailsLoadingSignal = signal(false);

  get upcomingBookings() {
    return this.#upcomingBookingsSignal.asReadonly();
  }

  get isUpcomingBookingsLoading() {
    return this.#isUpcomingBookingsLoadingSignal.asReadonly();
  }

  get pastBookings() {
    return this.#pastBookingsSignal.asReadonly();
  }

  get isPastBookingsLoading() {
    return this.#isPastBookingsLoadingSignal.asReadonly();
  }

  get bookingDetails() {
    return this.#bookingDetailsSignal.asReadonly();
  }

  get isBookingDetailsLoading() {
    return this.#isBookingDetailsLoadingSignal.asReadonly();
  }

  public refreshBookingDetails(bookingDetails: BookingRequestDetailsModel) {
    this.#bookingDetailsSignal.set(bookingDetails);
  }

  public getUpcomingBookings() {
    this.#isUpcomingBookingsLoadingSignal.set(true);
    return this.#bookingApiService.getUpcomingBookings().pipe(
      tap(bookings => {
        const convertedServices = bookings.map(booking => {
          return {
            ...booking,
            services: convertServicesToViewModel(booking.services || []),
          };
        });
        this.#upcomingBookingsSignal.set(convertedServices);
      }),
      finalize(() => this.#isUpcomingBookingsLoadingSignal.set(false))
    );
  }

  public getPastBookings() {
    this.#isPastBookingsLoadingSignal.set(true);
    return this.#bookingApiService.getPastBookings().pipe(
      tap(bookings => {
        const convertedServices = bookings.map(booking => {
          return {
            ...booking,
            services: convertServicesToViewModel(booking.services || []),
          };
        });
        this.#pastBookingsSignal.set(convertedServices);
      }),
      finalize(() => this.#isPastBookingsLoadingSignal.set(false))
    );
  }

  public getBookingDetails(bookingId: number) {
    this.#isBookingDetailsLoadingSignal.set(true);
    return this.#bookingApiService.getBookingDetails(bookingId).pipe(
      tap(bookingDetails => this.#bookingDetailsSignal.set(bookingDetails)),
      finalize(() => this.#isBookingDetailsLoadingSignal.set(false))
    );
  }

  public deleteBooking(bookingId: number) {
    this.#isBookingDetailsLoadingSignal.set(true);
    return this.#bookingApiService.deleteBooking(bookingId).pipe(
      tap(() => {
        this.#googleAnalyticsService.trackEvent(
          GoogleAnalyticsEventName.DeleteEvent,
          GoogleAnalyticsEventCategory.BookingRequest,
          `Request ID: ${bookingId}`
        );
      }),
      finalize(() => this.#isBookingDetailsLoadingSignal.set(false))
    );
  }
}

export function convertServicesToViewModel(
  services: Service[]
): ServiceViewModel[] {
  const convertedServices: ServiceViewModel[] = [];
  const servicesArray = structuredClone(SERVICE_MODEL_ARRAY);
  servicesArray.forEach(service => {
    const activeService = services?.find(activeService => {
      return activeService.serviceType == service.service;
    });
    if (activeService) {
      const currentValue = servicesArray.get(activeService.serviceType);
      if (currentValue) {
        servicesArray.set(activeService.serviceType, currentValue);
        if (activeService.url) {
          currentValue.description = activeService.url;
          currentValue.isUrl = true;
        }
        if (activeService.ticketName) {
          currentValue.ticketName = activeService.ticketName;
          currentValue.ticketStatus = activeService.ticketStatus;
        }
        convertedServices.push(currentValue);
      }
    }
  });
  return convertedServices;
}
